import { useEffect, useMemo, useRef, useState } from "react";
import { Card, ImagePlaceholder, Badge, Carousel, Separator } from "@hotelise/ui";
import { useIsMobile } from "@hotelise/ui";
import { ChevronRight, Package, Gift, Settings, Tag } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";

import ProductsClient from "services/api/products";
import FileUploadService from "services/storage/fileUpload";
import { getProperties } from "state/modules/properties";
import { getPropertiesList, getPropertiesLoaded } from "state/modules/properties/selectors";
import type { Property } from "models/properties";
import type { Product, ProductSubItem } from "interfaces/products";

/* ── Currency helpers ─────────────────────────────────────── */

const currencySymbol: Record<string, string> = { USD: "$", AMD: "\u058F", GBP: "\u00A3", EUR: "\u20AC" };
const fmt = (amount: number | undefined, currency: string) =>
	`${currencySymbol[currency] ?? ""}${(amount ?? 0).toLocaleString()}`;

/* ── Section Components ───────────────────────────────────── */

function WelcomePackSection({
	items,
	itemImages,
	productsById,
}: {
	items: ProductSubItem[];
	itemImages: Record<string, string>;
	productsById: Map<string, Product>;
}) {
	if (items.length === 0) return null;
	return (
		<section className="mt-2 space-y-3">
			<div className="flex items-center gap-2">
				<div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
					<Package className="h-4 w-4 text-primary" />
				</div>
				<div>
					<h3 className="font-semibold text-base">Welcome Pack</h3>
					<p className="text-xs text-muted-foreground">Turnover items supplied for each guest check-in. These are expensed to you.</p>
				</div>
			</div>

			<div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
				{items.map((item) => (
					<Card key={item.id} className="p-3 flex flex-col items-center text-center gap-2">
						<ImagePlaceholder
							src={itemImages[item.id]}
							alt={item.name}
							className="h-16 w-16 rounded-lg object-cover"
							aspectRatio=""
							iconSize="h-6 w-6"
							icon={Package}
						/>
						<div className="space-y-0.5">
							<span className="text-sm font-medium leading-tight">{item.name}</span>
							{productsById.get(item.id)?.description && (
								<p className="text-xs text-muted-foreground leading-relaxed">
									{productsById.get(item.id)?.description}
								</p>
							)}
						</div>
					</Card>
				))}
			</div>
		</section>
	);
}

function PropertySettingsSection({ property }: { property: Property }) {
	const settings = property.hotelise?.propertySettings;
	if (!settings) return null;

	const cleaningFees = settings.cleaningFees || [];
	const reportingCurrency = settings.reportingCurrency;
	const invoicingCurrency = settings.invoicingCurrency;

	const hasContent = reportingCurrency || invoicingCurrency || cleaningFees.length > 0;
	if (!hasContent) return null;

	return (
		<section className="mt-2 space-y-3">
			<div className="flex items-center gap-2">
				<div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
					<Settings className="h-4 w-4 text-primary" />
				</div>
				<h3 className="font-semibold text-base">Property Settings</h3>
			</div>

			<Card className="p-4">
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
					{reportingCurrency && (
						<div className="space-y-1">
							<span className="text-xs font-medium text-muted-foreground">Reporting Currency</span>
							<p className="text-sm font-medium">{reportingCurrency}</p>
						</div>
					)}
					{invoicingCurrency && (
						<div className="space-y-1">
							<span className="text-xs font-medium text-muted-foreground">Invoicing Currency</span>
							<p className="text-sm font-medium">{invoicingCurrency}</p>
						</div>
					)}
					{cleaningFees.map((fee) => (
						<div key={fee.id} className="space-y-1">
							<span className="text-xs font-medium text-muted-foreground">Cleaning Fee ({fee.currency})</span>
							<p className="text-sm font-medium tabular-nums">{fmt(fee.value, fee.currency as string)}</p>
						</div>
					))}
				</div>
			</Card>
		</section>
	);
}

function CustomDetailsSection({ property }: { property: Property }) {
	const categories = property.hotelise?.propertyDetails?.categories;
	if (!categories || categories.length === 0) return null;

	return (
		<section className="mt-2 space-y-3">
			<div className="flex items-center gap-2">
				<div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
					<Tag className="h-4 w-4 text-primary" />
				</div>
				<h3 className="font-semibold text-base">Custom Details</h3>
			</div>

			<div className="space-y-3">
				{categories.map((cat) => (
					<Card key={cat.id} className="p-4">
						<h4 className="text-sm font-semibold mb-3">{cat.name}</h4>
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
							{cat.fields.map((field) => (
								<div key={field.id} className="space-y-0.5">
									<span className="text-xs text-muted-foreground">{field.name}</span>
									<p className="text-sm font-medium">{field.value || "\u2014"}</p>
								</div>
							))}
						</div>
					</Card>
				))}
			</div>
		</section>
	);
}

function HoteliseSuppliedSection({
	items,
	itemImages,
	productsById,
}: {
	items: ProductSubItem[];
	itemImages: Record<string, string>;
	productsById: Map<string, Product>;
}) {
	return (
		<section className="relative rounded-xl border-2 border-emerald-500/40 bg-emerald-500/[0.04] dark:bg-emerald-500/[0.06] p-4 space-y-3">
			<div className="absolute -top-3 right-4">
				<Badge className="bg-emerald-600 hover:bg-emerald-600 text-white text-[11px] font-bold tracking-wider uppercase px-3 py-0.5 shadow-sm">
					Included Free
				</Badge>
			</div>

			<div className="flex items-center gap-2">
				<div className="h-8 w-8 rounded-full bg-emerald-500/15 flex items-center justify-center">
					<Gift className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
				</div>
				<div>
					<h3 className="font-semibold text-base">Supplied by Hotelise</h3>
					<p className="text-xs text-muted-foreground">
						These items and services are provided at{" "}
						<span className="font-semibold text-emerald-600 dark:text-emerald-400">no extra cost</span>
						{" \u2014 "}included with your service.
					</p>
				</div>
			</div>

			{items.length > 0 && (
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
					{items.map((item) => (
						<Card key={item.id} className="p-4 flex items-start gap-3 bg-card">
							<ImagePlaceholder
								src={itemImages[item.id]}
								alt={item.name}
								className="h-14 w-14 rounded-lg object-cover shrink-0"
								aspectRatio=""
								iconSize="h-5 w-5"
								icon={Gift}
							/>
							<div className="space-y-0.5 min-w-0">
								<span className="text-sm font-medium">{item.name}</span>
								{productsById.get(item.id)?.description && (
									<p className="text-xs text-muted-foreground leading-relaxed">
										{productsById.get(item.id)?.description}
									</p>
								)}
							</div>
						</Card>
					))}
				</div>
			)}
		</section>
	);
}

/* ── Property Details Visual ──────────────────────────────── */

function PropertyDetailsVisual({ property, wpSubProducts, wpSubProductImages, opWpProduct, opWpSubProducts, opWpSubProductImages, productsById }: {
	property: Property;
	wpSubProducts: ProductSubItem[];
	wpSubProductImages: Record<string, string>;
	opWpProduct: Product | null;
	opWpSubProducts: ProductSubItem[];
	opWpSubProductImages: Record<string, string>;
	productsById: Map<string, Product>;
}) {
	return (
		<div>
			{opWpProduct && (
				<>
					<HoteliseSuppliedSection
						items={opWpSubProducts}
						itemImages={opWpSubProductImages}
						productsById={productsById}
					/>
					<Separator className="my-5" />
				</>
			)}
			{wpSubProducts.length > 0 && (
				<>
					<WelcomePackSection items={wpSubProducts} itemImages={wpSubProductImages} productsById={productsById} />
					<Separator className="my-5" />
				</>
			)}
			<PropertySettingsSection property={property} />
			{property.hotelise?.propertyDetails?.categories?.length ? (
				<>
					<Separator className="my-5" />
					<CustomDetailsSection property={property} />
				</>
			) : null}
		</div>
	);
}

/* ── Page Component ───────────────────────────────────────── */

export default function ServicePropertyDetailsPage() {
	const isMobile = useIsMobile();
	const dispatch = useDispatch();
	const properties = useSelector(getPropertiesList);
	const propertiesLoaded = useSelector(getPropertiesLoaded);

	const [products, setProducts] = useState<Product[]>([]);
	const [productsLoaded, setProductsLoaded] = useState(false);
	const [selectedId, setSelectedId] = useState<string>("");
	const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
	const resolvedKeysRef = useRef(new Set<string>());

	useEffect(() => {
		document.title = "Property Details \u2014 Hotelise";
	}, []);

	useEffect(() => {
		if (!propertiesLoaded) {
			dispatch(getProperties());
		}
	}, [dispatch, propertiesLoaded]);

	useEffect(() => {
		ProductsClient.getProducts()
			.then((res) => {
				setProducts(res.data.content || []);
				setProductsLoaded(true);
			})
			.catch(() => {
				setProductsLoaded(true);
			});
	}, []);

	// Auto-select first property once loaded
	useEffect(() => {
		if (!selectedId && properties.length > 0) {
			setSelectedId(properties[0].id);
		}
	}, [properties, selectedId]);

	const productsById = useMemo(() => {
		const map = new Map<string, Product>();
		for (const p of products) {
			if (p.id) map.set(p.id, p);
		}
		return map;
	}, [products]);

	const selectedProperty = useMemo(
		() => properties.find((p) => p.id === selectedId) ?? properties[0],
		[properties, selectedId]
	);

	const wpId = selectedProperty?.hotelise?.propertySettings?.welcomePackId;
	const wpProduct = wpId ? productsById.get(wpId) ?? null : null;
	const wpSubProducts = useMemo(() => wpProduct?.subProducts ?? [], [wpProduct]);

	const opWpId = selectedProperty?.hotelise?.propertySettings?.operatorWelcomePackId;
	const opWpProduct = opWpId ? productsById.get(opWpId) ?? null : null;
	const opWpSubProducts = useMemo(() => opWpProduct?.subProducts ?? [], [opWpProduct]);

	const allWpSubProducts = useMemo(
		() => [...wpSubProducts, ...opWpSubProducts],
		[wpSubProducts, opWpSubProducts]
	);

	// Resolve the first image (S3 key -> signed URL) for each welcome pack sub-item
	useEffect(() => {
		const keysSet = new Set<string>();
		allWpSubProducts.forEach((item) => {
			const refProduct = productsById.get(item.id);
			const firstKey = refProduct?.imageKeys?.[0];
			if (firstKey && !resolvedKeysRef.current.has(firstKey)) {
				keysSet.add(firstKey);
			}
		});
		const keysToResolve = Array.from(keysSet);
		if (!keysToResolve.length) return;

		keysToResolve.forEach((key) => resolvedKeysRef.current.add(key));

		Promise.all(
			keysToResolve.map(async (key) => {
				try {
					const url = await FileUploadService.getSignedUrl(key);
					return [key, url] as const;
				} catch {
					resolvedKeysRef.current.delete(key);
					return null;
				}
			})
		).then((results) => {
			setImageUrls((prev) => {
				const next = { ...prev };
				results.forEach((result) => {
					if (result) next[result[0]] = result[1];
				});
				return next;
			});
		});
	}, [allWpSubProducts, productsById]);

	const wpSubProductImages = useMemo(() => {
		const map: Record<string, string> = {};
		wpSubProducts.forEach((item) => {
			const firstKey = productsById.get(item.id)?.imageKeys?.[0];
			if (firstKey && imageUrls[firstKey]) {
				map[item.id] = imageUrls[firstKey];
			}
		});
		return map;
	}, [wpSubProducts, productsById, imageUrls]);

	const opWpSubProductImages = useMemo(() => {
		const map: Record<string, string> = {};
		opWpSubProducts.forEach((item) => {
			const firstKey = productsById.get(item.id)?.imageKeys?.[0];
			if (firstKey && imageUrls[firstKey]) {
				map[item.id] = imageUrls[firstKey];
			}
		});
		return map;
	}, [opWpSubProducts, productsById, imageUrls]);

	if (!propertiesLoaded || !productsLoaded) {
		return (
			<div className="flex items-center justify-center py-20">
				<div className="text-sm text-muted-foreground">Loading...</div>
			</div>
		);
	}

	if (properties.length === 0) {
		return (
			<div className="space-y-4">
				<div>
					<h1 className="text-2xl font-semibold">Property Details</h1>
					<p className="text-sm text-muted-foreground">No properties found.</p>
				</div>
			</div>
		);
	}

	const renderPropertyCard = (prop: Property, isMobileCard?: boolean) => (
		<button
			key={prop.id}
			onClick={() => setSelectedId(prop.id)}
			className={`${isMobileCard ? "w-full" : "group"} text-left rounded-xl overflow-hidden border-2 transition-all ${
				!isMobileCard ? "hover:shadow-md" : ""
			} ${
				selectedId === prop.id
					? "border-primary shadow-md" + (!isMobileCard ? " ring-1 ring-primary/20" : "")
					: isMobileCard
					? "border-transparent"
					: "border-border"
			}`}
		>
			<ImagePlaceholder
				src={prop.picture}
				alt={prop.name}
				className={`w-full ${isMobileCard ? "h-32" : "h-40"} object-cover`}
				aspectRatio=""
			/>
			<div className={`${isMobileCard ? "p-3" : "p-4"} bg-card`}>
				<div className="flex items-center justify-between">
					<span className={`font-semibold ${isMobileCard ? "text-sm" : ""}`}>
						{prop.name}
					</span>
					{!isMobileCard && (
						<ChevronRight
							className={`h-4 w-4 text-muted-foreground transition-transform ${
								selectedId === prop.id
									? "rotate-90"
									: "group-hover:translate-x-0.5"
							}`}
						/>
					)}
				</div>
				<span className={`${isMobileCard ? "text-xs" : "text-sm"} text-muted-foreground line-clamp-1 block`}>
					{prop.address?.display || ""}
				</span>
			</div>
		</button>
	);

	/* ── Mobile ──────────────────────────────────────────── */
	if (isMobile) {
		return (
			<div className="space-y-4">
				<div>
					<h1 className="text-xl font-semibold">Property Details</h1>
					<p className="text-sm text-muted-foreground">Your property setup and operational terms.</p>
				</div>

				<Carousel className="w-full -mx-1" slideClassName="flex-[0_0_75%] min-w-0">
					{properties.map((prop) => renderPropertyCard(prop, true))}
				</Carousel>

				{selectedProperty && (
					<PropertyDetailsVisual
						property={selectedProperty}
						wpSubProducts={wpSubProducts}
						wpSubProductImages={wpSubProductImages}
						opWpProduct={opWpProduct}
						opWpSubProducts={opWpSubProducts}
						opWpSubProductImages={opWpSubProductImages}
						productsById={productsById}
					/>
				)}
			</div>
		);
	}

	/* ── Desktop ─────────────────────────────────────────── */
	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-2xl font-semibold">Property Details</h1>
				<p className="text-sm text-muted-foreground">Your property setup, welcome packs, and operational terms with Hotelise.</p>
			</div>

			{/* Property selector grid */}
			<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
				{properties.map((prop) => renderPropertyCard(prop))}
			</div>

			{/* Detail panel */}
			{selectedProperty && (
				<Card className="p-6">
					<div className="flex items-center gap-3 mb-4">
						<ImagePlaceholder
							src={selectedProperty.picture}
							alt={selectedProperty.name}
							className="h-12 w-12 rounded-lg object-cover"
							aspectRatio=""
							iconSize="h-5 w-5"
						/>
						<div>
							<h2 className="text-lg font-semibold">{selectedProperty.name}</h2>
							<p className="text-sm text-muted-foreground">{selectedProperty.address?.display || ""}</p>
						</div>
					</div>
					<PropertyDetailsVisual
						property={selectedProperty}
						wpSubProducts={wpSubProducts}
						wpSubProductImages={wpSubProductImages}
						opWpProduct={opWpProduct}
						opWpSubProducts={opWpSubProducts}
						opWpSubProductImages={opWpSubProductImages}
						productsById={productsById}
					/>
				</Card>
			)}
		</div>
	);
}
